import useSWR from 'swr';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

const fetcher = (url: string) => fetch(url, { credentials: 'include' }).then((res) => res.json());

export function useCredits() {
  const { data, error, isLoading, mutate } = useSWR<{ balance: number; limit: number; planName: string }>(
    '/api/credits',
    fetcher
  );

  return {
    balance: data?.balance ?? 0,
    limit: data?.limit ?? 0,
    planName: data?.planName ?? null,
    isLoading,
    error,
    mutate,
  };
}

export function CreditDisplay({ isSidebarOpen, onViewHistory }: { isSidebarOpen: boolean; onViewHistory?: () => void }) {
  const { balance, limit, planName, isLoading } = useCredits();

  const used = Math.max(0, limit - balance);
  const percentage = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const displayBalance = isLoading ? '...' : balance.toLocaleString('en-GB');
  const displayLimit = limit.toLocaleString('en-GB');

  const barColor = 'bg-meter-fill';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className={`flex items-center cursor-pointer rounded-full bg-input-bg control-gradient border border-control-border shadow-lg transition-all h-[40px] ${isSidebarOpen ? 'gap-2 px-3' : 'w-[40px] justify-center'}`}>
          <Tooltip open={!isSidebarOpen ? undefined : false}>
            <TooltipTrigger asChild>
              <img src="/stack-coin.svg" alt="Credits" className={`h-[22px] w-[22px] shrink-0 ${isSidebarOpen ? '-ml-1' : ''}`} />
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={10}>
              {displayBalance} credits remaining
            </TooltipContent>
          </Tooltip>
          {isSidebarOpen && (
            <span className="text-sm text-content-strong font-medium whitespace-nowrap">
              Credits
            </span>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="right" sideOffset={10} className="w-64 p-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">{planName || 'No'} Plan</span>
            <span className="text-xs text-foreground">{displayBalance} / {displayLimit}</span>
          </div>

          {/* Progress bar */}
          <div className="w-full h-2 bg-surface-track rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${barColor}`}
              style={{ width: `${100 - percentage}%` }}
            />
          </div>

          <p className="text-xs text-foreground">
            {used.toLocaleString('en-GB')} of {displayLimit} credits used this month
          </p>

          {percentage > 90 && (
            <p className="text-xs text-danger-foreground font-medium">Running low on credits</p>
          )}

          {onViewHistory && (
            <Button variant="secondary" size="sm" onClick={onViewHistory} className="w-full mt-1 text-xs h-8">
              View usage history
            </Button>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
